/**
 * /equipment/preuse-templates/:templateId — one pre-use template on its own
 * page (`GET /preuse-templates/:id`). Panels stack full width: the record's
 * facts on top, then its items — an editor while it is a draft, a read-only
 * list once it is live or archived.
 *
 * LIFECYCLE (the API's, preuse-templates.routes.ts):
 *   draft    — editable by admin / office staff. Save is PATCH (name, type,
 *              description) then PUT /items (the whole list, in order).
 *              Publish (admin only) makes it the type's live form and ARCHIVES
 *              the type's current live one in the same transaction. Delete
 *              removes a draft.
 *   active   — what the phone serves. Never edited: Duplicate makes a new
 *              draft to edit and publish. Archive (admin only) stops serving it.
 *   archived — kept for the record. Duplicate still works.
 *
 * Every refusal is put into a sentence by `preuseRefusalSentence`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ArrowDown, ArrowUp, Copy, Loader2, Plus, Save, Send, Trash2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import {
  useActiveEquipmentTypes,
  usePreuseMutations,
  usePreuseTemplate,
  usePreuseTemplates,
  useUserDisplayName,
} from '../../hooks/usePreuseTemplates';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canPublishPreuse, canWritePreuse, groupItemsBySection, preuseRefusalSentence } from '../../lib/preuse';
import type { PreuseItemWrite, PreuseTemplateDetail } from '../../types/preuse';
import { DH_BAND_SECONDARY, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, DhSpec } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { PREUSE_LIST, PreuseStatusBadge, typeName } from './PreuseTemplatesScreen';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';

interface EditItem {
  /** Client-only, for React keys and reorder. */
  ck: number;
  /** The stored key, kept on save so answers stay comparable across versions. Absent on a new row. */
  itemKey?: string;
  label: string;
  section: string;
  critical: boolean;
  required: boolean;
  helpText: string;
}

interface EditState {
  name: string;
  description: string;
  equipmentTypeId: string;
  items: EditItem[];
}

let nextCk = 1;
function toEdit(t: PreuseTemplateDetail): EditState {
  return {
    name: t.name,
    description: t.description ?? '',
    equipmentTypeId: t.equipmentTypeId,
    items: [...t.items]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((it) => ({
        ck: nextCk++,
        itemKey: it.itemKey,
        label: it.label,
        section: it.section ?? '',
        critical: it.critical,
        required: it.required,
        helpText: it.helpText ?? '',
      })),
  };
}

/** Compare everything but the client keys. */
const fingerprint = (e: EditState) => JSON.stringify({ ...e, items: e.items.map(({ ck, ...rest }) => rest) });

export function itemsToWrite(items: EditItem[]): PreuseItemWrite[] {
  return items.map((it) => ({
    ...(it.itemKey ? { item_key: it.itemKey } : {}),
    label: it.label.trim(),
    section: it.section.trim() || null,
    critical: it.critical,
    required: it.required,
    help_text: it.helpText.trim() || null,
  }));
}

function sourceLabel(t: PreuseTemplateDetail): string {
  if (t.source === 'starter') return t.starterKey ? `Starter list (${t.starterKey.replace(/_/g, ' ')}, v${t.starterVersion ?? '—'})` : 'Starter list';
  if (t.source === 'manual') return 'Written by hand';
  if (t.source === 'ai') return 'Built from a paper form';
  return '—';
}

function ItemsReadOnly({ t }: { t: PreuseTemplateDetail }) {
  if (t.items.length === 0) return <DhEmpty>This template has no items.</DhEmpty>;
  return (
    <div className="divide-y divide-fieldiq-border" data-testid="preuse-items-readonly">
      {groupItemsBySection(t.items).map((g) => (
        <div key={g.section} className="px-4 py-3">
          <p className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{g.section}</p>
          <ol className="space-y-1.5">
            {g.items.map((it) => (
              <li key={it.id} className="text-sm text-fieldiq-text">
                <span className="text-white">{it.label}</span>
                {it.critical && <CriticalPill />}
                <span className="ml-2 text-xs text-fieldiq-muted">{it.required ? 'required' : 'optional'}</span>
                {it.helpText && <span className="block text-xs text-fieldiq-muted">{it.helpText}</span>}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

export function CriticalPill() {
  return (
    <span className="ml-2 inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-red px-1.5 py-0.5 align-middle text-[0.6rem] font-black uppercase leading-none text-fi-signal-ink">
      Critical
    </span>
  );
}

function ItemEditor({ state, setState }: { state: EditState; setState: React.Dispatch<React.SetStateAction<EditState | null>> }) {
  const setItem = (ck: number, patch: Partial<EditItem>) =>
    setState((s) => (s ? { ...s, items: s.items.map((x) => (x.ck === ck ? { ...x, ...patch } : x)) } : s));
  const move = (i: number, d: -1 | 1) =>
    setState((s) => {
      if (!s) return s;
      const j = i + d;
      if (j < 0 || j >= s.items.length) return s;
      const items = [...s.items];
      [items[i], items[j]] = [items[j], items[i]];
      return { ...s, items };
    });
  const sections = useMemo(() => Array.from(new Set(state.items.map((x) => x.section.trim()).filter(Boolean))), [state.items]);
  return (
    <div className="space-y-3 p-4" data-testid="preuse-item-editor">
      <datalist id="preuse-sections">
        {sections.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {state.items.length === 0 && <p className="text-sm text-fieldiq-text">No items yet. A template needs at least one item to be published.</p>}
      {state.items.map((it, i) => (
        <div key={it.ck} className="grid gap-2 rounded-fi-control border border-fieldiq-border p-3 lg:grid-cols-[2rem_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.5fr)_auto_auto]" data-testid="preuse-edit-item">
          <span className="pt-2 font-mono text-sm text-fieldiq-muted">{i + 1}</span>
          <input className={DH_INPUT} aria-label={`Item ${i + 1} label`} placeholder="What to check *" value={it.label} maxLength={300} onChange={(e) => setItem(it.ck, { label: e.target.value })} />
          <input className={DH_INPUT} aria-label={`Item ${i + 1} section`} placeholder="Section" list="preuse-sections" value={it.section} maxLength={100} onChange={(e) => setItem(it.ck, { section: e.target.value })} />
          <input className={DH_INPUT} aria-label={`Item ${i + 1} help text`} placeholder="Help text (optional)" value={it.helpText} maxLength={2000} onChange={(e) => setItem(it.ck, { helpText: e.target.value })} />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-fieldiq-text">
              <input type="checkbox" aria-label={`Item ${i + 1} critical`} checked={it.critical} onChange={(e) => setItem(it.ck, { critical: e.target.checked })} />
              Critical
            </label>
            <label className="flex items-center gap-1.5 text-sm text-fieldiq-text">
              <input type="checkbox" aria-label={`Item ${i + 1} required`} checked={it.required} onChange={(e) => setItem(it.ck, { required: e.target.checked })} />
              Required
            </label>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="rounded-fi-control p-1.5 text-fieldiq-muted hover:bg-fieldiq-dark hover:text-white disabled:opacity-30" aria-label={`Move item ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
              <ArrowUp className="size-4" aria-hidden="true" />
            </button>
            <button type="button" className="rounded-fi-control p-1.5 text-fieldiq-muted hover:bg-fieldiq-dark hover:text-white disabled:opacity-30" aria-label={`Move item ${i + 1} down`} disabled={i === state.items.length - 1} onClick={() => move(i, 1)}>
              <ArrowDown className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded-fi-control p-1.5 text-fieldiq-muted hover:bg-fieldiq-dark hover:text-white"
              aria-label={`Remove item ${i + 1}`}
              onClick={() => setState((s) => (s ? { ...s, items: s.items.filter((x) => x.ck !== it.ck) } : s))}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className={DH_SECONDARY}
        onClick={() =>
          setState((s) =>
            s ? { ...s, items: [...s.items, { ck: nextCk++, label: '', section: s.items[s.items.length - 1]?.section ?? '', critical: false, required: true, helpText: '' }] } : s,
          )
        }
      >
        <Plus className="size-4" aria-hidden="true" />
        Add item
      </button>
      <p className="text-xs text-fieldiq-muted">
        A failed <strong>critical</strong> item fails the whole check. A <strong>required</strong> item must be answered — N/A is an answer.
      </p>
    </div>
  );
}

type Confirm = 'publish' | 'archive' | 'delete' | null;

export default function PreuseTemplateDetailScreen() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const back = useBackToList(PREUSE_LIST);
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canWrite = canWritePreuse(user?.role, elevated);
  const canPublish = canPublishPreuse(user?.role);
  const zone = useTenantTimeZone();

  const query = usePreuseTemplate(templateId);
  const failure = queryLoadFailure(query);
  const t = query.data ?? null;
  const isDraft = t?.status === 'draft';
  const editing = !!t && isDraft && canWrite;

  const types = useActiveEquipmentTypes();
  const publisher = useUserDisplayName(t?.publishedBy);
  const m = usePreuseMutations();
  const liveForType = usePreuseTemplates({ status: 'active', equipment_type_id: t?.equipmentTypeId }, !!t && isDraft && canPublish);
  const currentLive = (liveForType.data ?? []).find((x) => x.id !== t?.id) ?? null;

  const [edit, setEdit] = useState<EditState | null>(null);
  const [baseline, setBaseline] = useState<string>('');
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [error, setError] = useState<string | null>(null);

  // Start the editor from the record once per record — never on a refetch,
  // which would wipe a half-made edit.
  const resetFrom = (next: PreuseTemplateDetail | null | undefined) => {
    if (!next) return;
    const e = toEdit(next);
    setEdit(e);
    setBaseline(fingerprint(e));
  };
  useEffect(() => {
    resetFrom(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.id]);

  const dirty = !!edit && edit !== null && baseline !== '' && baseline !== fingerprint(edit);

  const save = async () => {
    if (!t || !edit) return;
    setError(null);
    if (!edit.name.trim()) return setError('The template needs a name.');
    if (!edit.equipmentTypeId) return setError('Choose the equipment type this template is for.');
    if (edit.items.some((x) => !x.label.trim())) return setError('Every item needs a label. Fill it in or remove the item.');
    const header: Record<string, string | null> = {};
    if (edit.name.trim() !== t.name) header.name = edit.name.trim();
    if ((edit.description.trim() || null) !== (t.description ?? null)) header.description = edit.description.trim() || null;
    if (edit.equipmentTypeId !== t.equipmentTypeId) header.equipment_type_id = edit.equipmentTypeId;
    try {
      const saved = await m.save.mutateAsync({ id: t.id, header, items: itemsToWrite(edit.items) });
      resetFrom(saved);
      toast.success('Draft saved');
    } catch (err) {
      setError(preuseRefusalSentence(err, 'The draft was not saved.'));
    }
  };

  const run = async (what: Exclude<Confirm, null>) => {
    if (!t) return;
    setError(null);
    try {
      if (what === 'publish') {
        await m.publish.mutateAsync(t.id);
        toast.success('Published — this is now the live form for the type');
      } else if (what === 'archive') {
        await m.archive.mutateAsync(t.id);
        toast.success('Archived');
      } else {
        await m.remove.mutateAsync(t.id);
        toast.success('Draft deleted');
        navigate(PREUSE_LIST, { replace: true });
        return;
      }
      setConfirm(null);
    } catch (err) {
      setConfirm(null);
      setError(preuseRefusalSentence(err, what === 'publish' ? 'The template was not published.' : what === 'archive' ? 'The template was not archived.' : 'The draft was not deleted.'));
    }
  };

  const duplicate = async () => {
    if (!t) return;
    setError(null);
    try {
      const copy = await m.duplicate.mutateAsync(t.id);
      toast.success('Copied into a new draft');
      if (copy?.id) navigate(`${PREUSE_LIST}/${copy.id}`, { state: { listUrl: PREUSE_LIST } });
    } catch (err) {
      setError(preuseRefusalSentence(err, 'The template was not copied.'));
    }
  };

  const actions = t ? (
    <div className="flex flex-wrap gap-2">
      {isDraft && canWrite && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setConfirm('delete')}>
          <Trash2 className="size-4" aria-hidden="true" />
          Delete draft
        </button>
      )}
      {!isDraft && canWrite && (
        <button type="button" className={DH_BAND_SECONDARY} disabled={m.duplicate.isPending} onClick={duplicate}>
          <Copy className="size-4" aria-hidden="true" />
          {t.status === 'active' ? 'Duplicate to edit' : 'Duplicate'}
        </button>
      )}
      {t.status === 'active' && canPublish && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setConfirm('archive')}>
          <Archive className="size-4" aria-hidden="true" />
          Archive
        </button>
      )}
      {editing && (
        <button type="button" className={DH_BAND_SECONDARY} disabled={!dirty || m.save.isPending} onClick={save}>
          <Save className="size-4" aria-hidden="true" />
          {m.save.isPending ? 'Saving…' : 'Save'}
        </button>
      )}
      {isDraft && canPublish && (
        <button
          type="button"
          className={PRIMARY}
          disabled={dirty || t.itemCount === 0}
          /* Why it is disabled is said in the items panel's footer and empty state,
             not in a native title (tooltip.source.test.ts). */
          onClick={() => setConfirm('publish')}
        >
          <Send className="size-4" aria-hidden="true" />
          Publish
        </button>
      )}
    </div>
  ) : undefined;

  const typeOptions = types.data ?? [];

  return (
    <div data-testid="preuse-template-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pre-use template"
        title={t?.name ?? (query.isLoading ? 'Loading…' : 'Pre-use template')}
        onBack={back}
        backLabel="Back to templates"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {error && (
            <p role="alert" className="rounded-fi-panel border border-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="preuse-error">
              {error}
            </p>
          )}

          <DhPanel eyebrow="Template" title="Details" testId="preuse-template-details">
            {!t && query.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading template" />
              </div>
            ) : !t && failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this template" />
            ) : !t ? (
              <DhEmpty>This template does not exist.</DhEmpty>
            ) : (
              <>
                {isDraft && !canPublish && (
                  <p className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">Only an admin can publish this draft.</p>
                )}
                {t.status === 'active' && (
                  <p className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                    This is the live form for every {typeName(t)} unit. A live template is not edited — duplicate it, edit the copy, and publish that.
                  </p>
                )}
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4 text-sm lg:grid-cols-4">
                  <DhSpec label="Status" value={<PreuseStatusBadge status={t.status} />} />
                  <DhSpec label="Equipment type" value={typeName(t)} />
                  <DhSpec label="Version" value={t.version === null ? '—' : `v${t.version}`} mono />
                  <DhSpec label="Items" value={`${t.itemCount} · ${t.criticalCount} critical`} />
                  <DhSpec label="Source" value={sourceLabel(t)} />
                  <DhSpec
                    label="Published"
                    value={
                      t.publishedAt
                        ? `${formatInstant(t.publishedAt, zone) ?? '—'} · ${publisher.data ?? (publisher.isLoading ? '…' : '—')}`
                        : '—'
                    }
                  />
                  <DhSpec label="Archived" value={formatInstant(t.archivedAt, zone) ?? '—'} />
                  <DhSpec label="Created" value={formatInstant(t.createdAt, zone) ?? '—'} />
                </dl>
                {!editing && t.description && <p className="px-4 pb-4 text-sm text-fieldiq-text">{t.description}</p>}
              </>
            )}
          </DhPanel>

          {t && editing && edit && (
            <>
              <DhPanel eyebrow="Draft" title="Name and type" testId="preuse-draft-header">
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <DhField label="Name *">
                    <input className={DH_INPUT} value={edit.name} maxLength={200} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                  </DhField>
                  <DhField label="Equipment type *">
                    <select className={DH_INPUT} value={edit.equipmentTypeId} onChange={(e) => setEdit({ ...edit, equipmentTypeId: e.target.value })}>
                      {!typeOptions.some((x) => x.id === edit.equipmentTypeId) && <option value={edit.equipmentTypeId}>{typeName(t)}</option>}
                      {typeOptions.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                  </DhField>
                  <div className="sm:col-span-2">
                    <DhField label="Description">
                      <textarea className={`${DH_INPUT} h-20 py-2`} value={edit.description} maxLength={2000} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                    </DhField>
                  </div>
                </div>
              </DhPanel>
              <DhPanel
                eyebrow="Draft"
                title="Items, in the order the crew sees them"
                footer={dirty ? 'Unsaved changes — Save before publishing.' : `${edit.items.length} ${edit.items.length === 1 ? 'item' : 'items'} · ${edit.items.filter((x) => x.critical).length} critical`}
              >
                <ItemEditor state={edit} setState={setEdit} />
              </DhPanel>
            </>
          )}

          {t && !editing && (
            <DhPanel eyebrow={t.status === 'draft' ? 'Draft' : 'Template'} title="Items">
              <ItemsReadOnly t={t} />
            </DhPanel>
          )}
        </div>
      </section>

      {t && (
        <DhDialog
          open={confirm !== null}
          title={confirm === 'publish' ? `Publish “${t.name}”?` : confirm === 'archive' ? `Archive “${t.name}”?` : `Delete “${t.name}”?`}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className={DH_SECONDARY} onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={PRIMARY}
                disabled={m.publish.isPending || m.archive.isPending || m.remove.isPending}
                onClick={() => confirm && run(confirm)}
              >
                {confirm === 'publish' ? 'Publish' : confirm === 'archive' ? 'Archive' : 'Delete draft'}
              </button>
            </>
          }
        >
          {confirm === 'publish' ? (
            <div className="space-y-2 text-sm text-fieldiq-text" data-testid="preuse-publish-confirm">
              <p>Crews will fill in this form on every {typeName(t)} pre-use and post-use check.</p>
              {currentLive ? (
                <p>
                  It replaces the type’s current live template, “{currentLive.name}”{currentLive.version !== null ? ` (v${currentLive.version})` : ''}, which is archived.
                </p>
              ) : (
                <p>
                  It replaces the type’s current live template, if there is one, which is archived.
                  {!liveForType.isLoading && !liveForType.isError && ` ${typeName(t)} has no live template now, so this becomes its first.`}
                </p>
              )}
              <p>Once published it cannot be edited; duplicate it to make changes.</p>
            </div>
          ) : confirm === 'archive' ? (
            <p className="text-sm text-fieldiq-text">
              Crews stop getting this form for {typeName(t)} units, and their checks go back to the plain pass / needs repair / fail check until another template is published. Checks already filled in keep their answers.
            </p>
          ) : (
            <p className="text-sm text-fieldiq-text">The draft and its items are removed. Nothing a crew has filled in is affected.</p>
          )}
        </DhDialog>
      )}
    </div>
  );
}
